import { TrashIcon } from "@heroicons/react/24/outline"
import { type RefObject, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import {
  type AdForgeDefaultCtaRule,
  type AdForgeDefaults,
  useAdForgeGetDefaultsQuery,
  useAdForgeUpdateDefaultsMutation,
} from "~/ui-rtk/api/adForgeApi"
import {
  MetaObjective,
  MetaOptimizationGoal,
} from "~/ui-rtk/api/types/custom-attribution-enums"
import { CallToAction } from "~/ui-rtk/api/types/paid-media.types"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { useGetTagCategoriesQuery } from "~/ui-rtk/api/tagCategoriesApi"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"
import {
  composeFromTemplate,
  extractSlugs,
} from "~/ui-rtk/pages/AdForge/components/adNameTemplate"

/**
 * Company-level AdForge defaults, embedded in Settings → Ad Blaster (moved
 * out of the AdBlaster page's modal). Sections:
 *   1. Default destination URL — seeded into AdForge's fresh-build mode when
 *      the form is empty so the editor doesn't have to retype the same URL
 *      every time.
 *   2. Ad name template — powers the "Build ad name from tags" composer.
 *   3. CTA rules matrix — one rule per (campaign objective, ad-set
 *      optimization goal) pair, mapping to a CallToAction. The AdBlaster
 *      blast modal uses this to pre-select the right CTA per destination.
 *
 * Persists at `company.metadata.settings.adForgeDefaults`.
 */

// Format enum keys (e.g. "OFFSITE_CONVERSIONS") as "Offsite conversions" for
// display. Matches the AdForge CTA picker's transform so the labels read
// consistently across the app.
function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase())
}

const OBJECTIVE_OPTIONS = Object.entries(MetaObjective).map(([key, value]) => ({
  value,
  label: humanize(key),
}))

const GOAL_OPTIONS = Object.entries(MetaOptimizationGoal).map(
  ([key, value]) => ({
    value,
    label: humanize(key),
  }),
)

const CTA_OPTIONS = Object.entries(CallToAction).map(([key, value]) => ({
  value,
  label: humanize(key),
}))

// The rules matrix additionally offers LIKE_PAGE, which isn't in the shared
// CallToAction enum (AdForge's fresh-creation picker excludes it). It only
// delivers on Page Likes ad sets with a plain-object_story_spec creative —
// the API ignores the rule anywhere else — so it's a per-rule choice, never
// offered as the company-wide default CTA.
const RULE_CTA_OPTIONS = [
  ...CTA_OPTIONS,
  { value: "LIKE_PAGE", label: "Like page (Page Likes ad sets only)" },
].sort((a, b) => a.label.localeCompare(b.label))

// Editor-side fallback when the company hasn't picked a defaultCta. Mirrors
// the fallback in AdForgePage so the empty state is honest about what
// AdForge would actually use.
const FALLBACK_CTA = "LEARN_MORE"
const DEFAULT_CTA = CTA_OPTIONS[0]?.value ?? FALLBACK_CTA

// React rows need a stable key even when two rules share the same (objective,
// goal, cta). A monotonically increasing counter avoids using array indices —
// only used as the React key, never sent to the server.
type EditableRule = AdForgeDefaultCtaRule & { uiKey: string }
let uiKeySeq = 0
function makeUiKey(): string {
  uiKeySeq += 1
  return `rule-${uiKeySeq}`
}

export default function AdForgeDefaultsSection() {
  const { data, isFetching } = useAdForgeGetDefaultsQuery()
  const [updateDefaults, { isLoading: isSaving }] =
    useAdForgeUpdateDefaultsMutation()

  const [destinationUrl, setDestinationUrl] = useState("")
  const [defaultCta, setDefaultCta] = useState<string>(FALLBACK_CTA)
  const [rules, setRules] = useState<EditableRule[]>([])
  const [nameTemplate, setNameTemplate] = useState("")
  const templateRef = useRef<HTMLInputElement>(null)
  // When the company has a structured naming convention (Company Settings →
  // Ad naming & tags), THAT is the source of truth and the template shown here
  // is derived from it — so it renders read-only rather than offering an edit
  // that would silently discard the positions.
  const isTemplateManaged = !!data?.adNaming?.positions?.length

  // Tag categories power the ad-name template — insert chips and the live
  // preview's sample values.
  const { data: tagCategories } = useGetTagCategoriesQuery()
  const categories = useMemo(() => tagCategories ?? [], [tagCategories])
  const orderedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  )

  // Sync local form state with the server payload whenever the cached value
  // changes (initial load, or after another tab saved).
  useEffect(() => {
    setDestinationUrl(data?.destinationUrl ?? "")
    setDefaultCta(data?.defaultCta ?? FALLBACK_CTA)
    setRules(
      (data?.ctaRules ?? []).map(rule => ({ ...rule, uiKey: makeUiKey() })),
    )
    setNameTemplate(data?.nameTemplate ?? "")
  }, [data])

  // Insert a `{slug}` placeholder at the input caret (fall back to appending).
  const insertPlaceholder = (slug: string) => {
    const token = `{${slug}}`
    const input = templateRef.current
    setNameTemplate(prev => {
      if (!input) return prev + token
      const start = input.selectionStart ?? prev.length
      const end = input.selectionEnd ?? prev.length
      const next = prev.slice(0, start) + token + prev.slice(end)
      // Restore the caret just after the inserted token on the next tick.
      requestAnimationFrame(() => {
        const pos = start + token.length
        input.focus()
        input.setSelectionRange(pos, pos)
      })
      return next
    })
  }

  // Known category slugs, and any {slug} in the template that doesn't match one
  // (surfaced as a non-blocking warning — the category may be added later).
  const knownSlugs = useMemo(
    () => new Set(categories.map(c => c.slug)),
    [categories],
  )
  const unknownSlugs = useMemo(
    () => extractSlugs(nameTemplate).filter(slug => !knownSlugs.has(slug)),
    [nameTemplate, knownSlugs],
  )

  // Live preview using each referenced category's first option label as a
  // stand-in for a real selection.
  const templatePreview = useMemo(() => {
    if (!nameTemplate.trim()) return ""
    const sample: Record<string, string> = {}
    for (const category of categories) {
      const label = category.options[0]?.label
      if (label) sample[category.slug] = formatTagLabel(label)
    }
    return composeFromTemplate(nameTemplate, sample)
  }, [nameTemplate, categories])

  // Lookup of all (objective, goal) pairs already claimed by some row. Each
  // row also indexes which pairs were claimed by *others* so its own
  // goal-options list can filter against that and never offer a duplicate.
  const takenPairs = useMemo(() => {
    const map = new Map<string, string>() // pair -> uiKey of the row that owns it
    for (const rule of rules) {
      map.set(`${rule.objective}|${rule.optimizationGoal}`, rule.uiKey)
    }
    return map
  }, [rules])

  // For a given objective, which goals are still unused (by other rows).
  // Always allows `currentGoal` through so the dropdown can show the value
  // the row already has even if the user is mid-edit.
  const availableGoalsFor = (
    objective: string,
    ownUiKey: string,
    currentGoal: string,
  ) =>
    GOAL_OPTIONS.filter(opt => {
      if (opt.value === currentGoal) return true
      const owner = takenPairs.get(`${objective}|${opt.value}`)
      return !owner || owner === ownUiKey
    })

  // Picks the first (objective, goal) pair that isn't already in use.
  // Returns null when every pair is claimed (9 × 23 = 207 cells, so in
  // practice never hit, but the Add button disables on null).
  const findFreePair = (): { objective: string; goal: string } | null => {
    for (const obj of OBJECTIVE_OPTIONS) {
      for (const goal of GOAL_OPTIONS) {
        if (!takenPairs.has(`${obj.value}|${goal.value}`)) {
          return { objective: obj.value, goal: goal.value }
        }
      }
    }
    return null
  }

  const freePair = findFreePair()
  const canAddRule = freePair !== null

  const addRule = () => {
    if (!freePair) return
    setRules(prev => [
      ...prev,
      {
        objective: freePair.objective,
        optimizationGoal: freePair.goal,
        callToAction: DEFAULT_CTA,
        uiKey: makeUiKey(),
      },
    ])
  }

  // When the objective changes on an existing row, the row's current goal
  // may collide with a pair taken by another row. In that case, swap the
  // goal to the first available one so the row stays valid.
  const updateRule = (uiKey: string, patch: Partial<AdForgeDefaultCtaRule>) => {
    setRules(prev =>
      prev.map(rule => {
        if (rule.uiKey !== uiKey) return rule
        const next = { ...rule, ...patch }
        if (patch.objective !== undefined) {
          const pair = `${next.objective}|${next.optimizationGoal}`
          const owner = takenPairs.get(pair)
          if (owner && owner !== uiKey) {
            const firstAvailable = GOAL_OPTIONS.find(
              opt => !takenPairs.has(`${next.objective}|${opt.value}`),
            )
            if (firstAvailable) next.optimizationGoal = firstAvailable.value
          }
        }
        return next
      }),
    )
  }

  const removeRule = (uiKey: string) => {
    setRules(prev => prev.filter(rule => rule.uiKey !== uiKey))
  }

  const handleSave = async () => {
    try {
      // Always send the trimmed string (including ""). The server treats
      // undefined as "preserve" and "" as "clear" — converting to undefined
      // here would silently swallow a user wiping the field.
      const payload: Parameters<typeof updateDefaults>[0] = {
        destinationUrl: destinationUrl.trim(),
        defaultCta: defaultCta.trim(),
        // Omitted when the template is managed by the structured convention in
        // Company Settings — sending it would tell the server the freeform
        // string is now authoritative and drop the saved positions, wiping the
        // convention as a side effect of editing an unrelated CTA rule.
        ...(isTemplateManaged ? {} : { nameTemplate: nameTemplate.trim() }),
        ctaRules: rules.map(
          ({ objective, optimizationGoal, callToAction }) => ({
            objective,
            optimizationGoal,
            callToAction,
          }),
        ),
      }
      const saved: AdForgeDefaults = await updateDefaults(payload).unwrap()
      setDestinationUrl(saved.destinationUrl ?? "")
      setDefaultCta(saved.defaultCta ?? FALLBACK_CTA)
      setNameTemplate(saved.nameTemplate ?? "")
      setRules(saved.ctaRules.map(rule => ({ ...rule, uiKey: makeUiKey() })))
      toast.success("AdForge defaults saved")
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "Failed to save defaults"
      toast.error(message)
    }
  }

  return (
    <section className="rounded-lg border border-system-blue-700 bg-system-blue-800 text-white">
      <div className="border-b border-system-blue-700 px-system-lg py-system-md">
        <h2 className="text-lg font-semibold">AdForge defaults</h2>
        <p className="text-system-gray-300 text-xs">
          Apply company-wide defaults so editors don&apos;t have to retype the
          same values on every draft.
        </p>
      </div>

      <div className="flex flex-col gap-system-lg px-system-lg py-system-md">
        {isFetching && !data ? (
          <div className="py-system-lg text-center text-system-gray-300">
            Loading…
          </div>
        ) : (
          <>
            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="adforge-default-url"
                className="text-sm font-medium text-white"
              >
                Default destination URL
              </label>
              <p className="text-system-gray-300 text-xs">
                Pre-fills AdForge&apos;s URL field on fresh drafts. Editors can
                override it before saving.
              </p>
              <Input
                id="adforge-default-url"
                value={destinationUrl}
                onChange={e => setDestinationUrl(e.target.value)}
                placeholder="https://example.com/landing-page"
              />
            </section>

            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="adforge-default-cta"
                className="text-sm font-medium text-white"
              >
                Default call to action
              </label>
              <p className="text-system-gray-300 text-xs">
                Used as the initial CTA on fresh AdForge drafts and as the
                fallback at blast time when no rule below matches the
                destination ad set. Defaults to &quot;Learn more&quot; when
                unset.
              </p>
              <Select
                id="adforge-default-cta"
                options={CTA_OPTIONS}
                value={defaultCta}
                onChange={e => setDefaultCta(e.target.value)}
              />
            </section>

            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="adforge-name-template"
                className="text-sm font-medium text-white"
              >
                Ad name template
              </label>
              {isTemplateManaged ? (
                <>
                  <p className="text-system-gray-300 text-xs">
                    Managed by the naming convention in{" "}
                    <Link
                      to={brandRoutes.SETTINGS.COMPANY}
                      className="font-medium text-brand hover:underline"
                    >
                      Company Settings → Ad naming
                    </Link>
                    , where positions and the separator are edited. Shown here
                    for reference.
                  </p>
                  <div className="rounded-md border border-system-blue-700 bg-system-blue-900 px-system-sm py-system-2xs font-mono text-sm text-white break-all">
                    {nameTemplate || "—"}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-system-gray-300 text-xs">
                    Define how AdForge&apos;s &quot;Build ad name from
                    tags&quot; composer assembles names. Use{" "}
                    <code className="text-system-gray-200">{"{slug}"}</code>{" "}
                    placeholders for tag categories; everything else (e.g.{" "}
                    <code className="text-system-gray-200">/</code> or{" "}
                    <code className="text-system-gray-200">-</code>) is kept
                    literally. Empty placeholders and their separators are
                    dropped. Leave blank to join selected tags with a separator.
                    For a simple ordered convention, use{" "}
                    <Link
                      to={brandRoutes.SETTINGS.COMPANY}
                      className="font-medium text-brand hover:underline"
                    >
                      Company Settings
                    </Link>{" "}
                    instead — it also lets you read tags back out of existing ad
                    names.
                  </p>
                  <Input
                    id="adforge-name-template"
                    itemRef={
                      templateRef as string & RefObject<HTMLInputElement>
                    }
                    fullWidth
                    value={nameTemplate}
                    onChange={e => setNameTemplate(e.target.value)}
                    placeholder="{gender}/{talent}-{creative_name}"
                  />
                </>
              )}

              {!isTemplateManaged && orderedCategories.length > 0 && (
                <div className="mt-system-2xs flex flex-wrap items-center gap-system-2xs">
                  <span className="text-system-gray-400 text-xs">Insert:</span>
                  {orderedCategories.map(category => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => insertPlaceholder(category.slug)}
                      className="rounded-md border border-system-blue-600 bg-system-blue-700/40 px-system-2xs py-[2px] text-xs text-system-gray-100 hover:bg-system-blue-600"
                      title={`Insert {${category.slug}}`}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              )}

              {!isTemplateManaged && unknownSlugs.length > 0 && (
                <p className="text-system-yellow-400 text-xs">
                  Unknown placeholder
                  {unknownSlugs.length > 1 ? "s" : ""}:{" "}
                  {unknownSlugs.map(slug => `{${slug}}`).join(", ")} — no tag
                  category matches. It will be dropped at compose time.
                </p>
              )}

              {templatePreview && (
                <div>
                  <span className="text-system-gray-400 text-xs">Preview</span>
                  <div className="mt-[2px] rounded-md border border-system-blue-700 bg-system-blue-900 px-system-sm py-system-2xs font-mono text-sm text-white break-all">
                    {templatePreview}
                  </div>
                </div>
              )}
            </section>

            <section className="flex flex-col gap-system-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-white">
                    Default CTA by objective + goal
                  </h3>
                  <p className="text-system-gray-300 text-xs">
                    Each rule pairs a Meta campaign objective with an ad-set
                    optimization goal and picks the call-to-action that suits
                    them.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addRule}
                  disabled={!canAddRule}
                  className="rounded-md bg-system-blue-700 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-blue-600 disabled:opacity-50"
                  title={
                    canAddRule
                      ? undefined
                      : "Every (objective, goal) pair is already covered"
                  }
                >
                  + Add rule
                </button>
              </div>

              {rules.length === 0 ? (
                <div className="rounded-md border border-dashed border-system-blue-700 px-system-md py-system-lg text-center text-system-gray-300 text-sm">
                  No CTA rules yet. Add one to override the default CTA for
                  specific (objective, goal) pairs.
                </div>
              ) : (
                <div className="flex flex-col gap-system-2xs">
                  <div className="grid grid-cols-[1fr_1fr_1fr_2.5rem] gap-system-sm text-system-gray-300 text-[11px] uppercase tracking-wide">
                    <span>Objective</span>
                    <span>Optimization goal</span>
                    <span>Call to action</span>
                    <span />
                  </div>
                  {rules.map(rule => {
                    const goalOptions = availableGoalsFor(
                      rule.objective,
                      rule.uiKey,
                      rule.optimizationGoal,
                    )
                    return (
                      <div
                        key={rule.uiKey}
                        className="grid grid-cols-[1fr_1fr_1fr_2.5rem] items-center gap-system-sm rounded-md border border-system-blue-700 px-system-sm py-system-2xs"
                      >
                        <Select
                          options={OBJECTIVE_OPTIONS}
                          value={rule.objective}
                          onChange={e =>
                            updateRule(rule.uiKey, {
                              objective: e.target.value,
                            })
                          }
                        />
                        <Select
                          options={goalOptions}
                          value={rule.optimizationGoal}
                          onChange={e =>
                            updateRule(rule.uiKey, {
                              optimizationGoal: e.target.value,
                            })
                          }
                        />
                        <Select
                          options={RULE_CTA_OPTIONS}
                          value={rule.callToAction}
                          onChange={e =>
                            updateRule(rule.uiKey, {
                              callToAction: e.target.value,
                            })
                          }
                        />
                        <button
                          type="button"
                          onClick={() => removeRule(rule.uiKey)}
                          className="flex items-center justify-center rounded-md text-system-gray-300 hover:text-system-red-400"
                          aria-label="Remove rule"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <div className="flex items-center justify-end border-t border-system-blue-700 px-system-lg py-system-md">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  )
}
